import clsx from 'clsx';
import {
    useController,
    Control,
    RegisterOptions,
    Path,
    FieldValues,
} from 'react-hook-form';
import {HTMLInputTypeAttribute} from 'react';
import {SuccessIcon, WarningIcon} from '../../icons';
import * as styles from './input.module.css';
import {useFormFieldWarning} from '../../../../hooks/useFormFieldWarning';

interface IInputProps<T> {
    name: Path<T>,
    control: Control<FieldValues>,
    type?: HTMLInputTypeAttribute,
    defaultValue?: Path<FieldValues>,
    icon?: JSX.Element,
    rules?: RegisterOptions,
    warningCallback?: (value: string | number) => string | false | Promise<string | false>,
    caption?: string,
    placeholder?: string,
    title?: string,
    disabled?: boolean,
    setValueAs?: (value: string | number | null) => string | number | '',
}

const Input = <T extends Record<string, any> >({
    caption,
    control,
    rules = {},
    warningCallback,
    defaultValue,
    name,
    icon: Icon,
    placeholder,
    type = 'text',
    title,
    setValueAs,
    disabled = false,
}: IInputProps<T>) => {
    const {
        field: {
            onChange,
            onBlur,
            value,
            ref,
        },
        fieldState: {
            error,
        },
    } = useController<FieldValues>({
        name,
        control,
        rules,
        defaultValue,
    });
    const {warning, triggerWarning} = useFormFieldWarning<FormData, string>({
        name,
        validate: (toValidate: string) => {
            if (!toValidate || !warningCallback) {
                return false;
            }

            return warningCallback(toValidate);
        },
    });

    const isError = error !== undefined;
    const isSuccess = value && !isError;

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value || '';
        const sanitizedValue = setValueAs ? setValueAs(newValue) : newValue;

        onChange(sanitizedValue);

        triggerWarning();
    };

    return (
        <span className={styles.root}>
            <span
                className={clsx(
                    styles.inputWrapper,
                    !title && styles.noTitle,
                )}
            >
                <span className={styles.title}>
                    {title}
                </span>
                {Icon && (
                    <span
                        className={clsx(
                            styles.prefixIcon,
                            value && styles.filled,
                        )}
                    >
                        {Icon}
                    </span>
                )}
                <input
                    ref={ref}
                    type={type}
                    name={name}
                    defaultValue={defaultValue}
                    placeholder={placeholder}
                    onChange={handleOnChange}
                    onBlur={onBlur}
                    onFocus={onBlur}
                    className={clsx(
                        styles.input,
                        disabled && styles.fontGray,
                        Icon && styles.withIcon,
                        isError && styles.error,
                        isSuccess && styles.success,
                    )}
                    value={value || ''}
                    disabled={disabled}
                />
                <span
                    className={clsx(
                        styles.statusIcon,
                        isError && styles.error,
                        isSuccess && styles.success,
                    )}
                >
                    {isError && (<WarningIcon/>)}
                    {isSuccess && (<SuccessIcon/>)}
                </span>
                <span
                    className={clsx(
                        styles.messages,
                    )}
                >
                    {isError && (<div className={styles.error}>{error && error.message}</div>)}
                    {warning && (<div className={styles.warning}>{warning}</div>)}
                    {(!error && !warning && caption) && (<div className={styles.warning}>{caption}</div>)}
                </span>
            </span>
        </span>
    );
};

export default Input;
